import classNames from "classnames";
import { PropsWithChildren, useMemo } from "react";
import { JournalAttribute, ListAttributesFunctionResponse } from "shared/request/myHealthyAdvantageApi";
import { useMatchMedia } from "shared/UI/Hooks/useMatchMedia";
import { HorizontalScroll } from "shared/UI/HorizontalScroll/HorizontalScroll";
import { NarrowContainer } from "shared/UI/LayoutComponents/LayoutComponents";
import { ChipProp, MultiSelectChips } from "shared/UI/MultiSelectChips/MultiSelectChips";
import { ResponsiveBreakpoints } from "shared/UI/ResponsiveBreakpoints";

interface JournalAttributesProps {
  data?: ListAttributesFunctionResponse;
  selectedAttributes: JournalAttribute[];
  handleSelectedAttributes: (selectedAttributes: JournalAttribute[]) => void;
}

const NoContainer = ({ children }: PropsWithChildren) => <>{children}</>;

export const JournalAttributes = ({ data, selectedAttributes, handleSelectedAttributes }: JournalAttributesProps) => {
  const isContainerDisplay = useMatchMedia(ResponsiveBreakpoints.Container);

  const groups = useMemo(
    () =>
      data?.groups.map((group) => ({
        ...group,
        attributes: group.attributes.map((attr) => ({ ...attr, isSelected: !!selectedAttributes.find((selAttr) => selAttr.id === attr.id) })),
      })),
    [data?.groups, selectedAttributes]
  );

  const handleSelectChip = (chip: ChipProp) => {
    if (selectedAttributes.find((attr) => attr.id === chip.id)) {
      const selection = [...selectedAttributes];

      const index = selection.findIndex((attr) => attr.id === chip.id);
      selection.splice(index, 1);
      handleSelectedAttributes(selection);
    } else {
      handleSelectedAttributes([...selectedAttributes, { id: chip.id, key: chip.key }]);
    }
  };

  const MultiChipsContainer = isContainerDisplay ? NarrowContainer : NoContainer;

  if (!groups || groups.length === 0) return null;

  return (
    <div data-testid="journal-attributes" className="space-y-6">
      {groups.map((group) => (
        <div key={group.id} className="space-y-4">
          <div className="w-full mx-auto max-w-[500px] lg:max-w-[800px] px-6 lg:px-0">
            <NarrowContainer>
              <p className="text-sm">{group.title}</p>
            </NarrowContainer>
          </div>
          <MultiChipsContainer>
            <HorizontalScroll className={classNames("journal-chips-scrollbar sm:mx-auto sm:max-w-[500px]")}>
              <MultiSelectChips className="w-[1300px] sm:pb-4 pl-6 lg:pl-0" chips={group.attributes} onSelect={handleSelectChip} />
            </HorizontalScroll>
          </MultiChipsContainer>
        </div>
      ))}
    </div>
  );
};
